import React, { useState, useEffect } from "react";
import { apiService } from "../../services/apiService";
import Toast from "../ui/Toast";

const DeleteAllAddressesModal = ({ onClose, onSuccess, tenant, tenantId }) => {
  const [loading, setLoading] = useState(true);
  const [previewData, setPreviewData] = useState(null);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState(null);

  // Extract tenant_id from props (prefer explicit tenantId, fallback to tenant object properties)
  const effectiveTenantId = tenantId || tenant?.id || tenant?.tenant_id || tenant?.tenantId;

  // Fetch preview data on mount
  useEffect(() => {
    if (effectiveTenantId) {
      fetchPreview();
    } else {
      setError("Tenant ID is required to delete addresses");
      setLoading(false);
      console.error("DeleteAllAddressesModal: No tenant_id available", {
        tenantId,
        tenant,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveTenantId]);

  const fetchPreview = async () => {
    setLoading(true);
    setError(null);

    try {
      // Pass tenant_id to preview API
      const response = await apiService.getDeleteAllAddressesPreview(effectiveTenantId);

      if (response.success) {
        setPreviewData(response.data);
      } else {
        setError(response.message || "Failed to load preview");
      }
    } catch (err) {
      console.error("Error fetching delete preview:", err);
      setError(err.message || "Failed to load preview");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!effectiveTenantId) {
      setToast({
        message: "Tenant ID is required for deletion",
        type: "error",
      });
      return;
    }

    setDeleting(true);

    try {
      // Pass tenant_id to delete API
      const response = await apiService.deleteAllAddresses(effectiveTenantId);

      if (response.success) {
        setToast({
          message: "All addresses deleted successfully!",
          type: "success",
        });
        
        // Wait for toast to be visible, then close modal and refresh
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 1000);
      } else {
        setToast({
          message: response.message || "Failed to delete addresses",
          type: "error",
        });
        setDeleting(false);
      }
    } catch (err) {
      console.error("Error deleting addresses:", err);
      let errorMessage = "Failed to delete addresses";
      if (err.isNetworkError || err.message === "Unable to load the data") {
        errorMessage = "Unable to load the data";
      } else if (err.status !== 400 && err.status !== 500) {
        errorMessage = err.message || errorMessage;
      }
      setToast({
        message: errorMessage,
        type: "error",
      });
      setDeleting(false);
    }
  };

  return (
    <>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
          <h3 className="text-lg font-bold text-gray-900 mb-4">
            Confirm Delete All Addresses
          </h3>

          {loading && (
            <div className="text-center py-4">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              <p className="mt-2 text-sm text-gray-600">Loading...</p>
            </div>
          )}

          {error && (
            <div className="mb-4">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {!loading && !error && previewData && (
            <div className="mb-6">
              {previewData.addressCount === 0 ? (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded">
                  <p className="text-sm text-blue-800">
                    There are no addresses to delete for your tenant.
                  </p>
                </div>
              ) : (
                <>
                  <p className="text-sm text-gray-700 mb-3">
                    You are about to delete{" "}
                    <strong className="text-gray-900">
                      {previewData.addressCount}
                    </strong>{" "}
                    address(es) from the master address table for your tenant.
                  </p>
                  <div className="bg-gray-50 p-3 rounded border border-gray-200 space-y-2">
                    <div className="text-sm">
                      <span className="font-semibold text-gray-700">
                        Inventory records affected:{" "}
                      </span>
                      <span className="text-gray-900 font-bold">
                        {previewData.inventoryCount}
                      </span>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          <div className="flex gap-2 justify-end">
            <button
              onClick={onClose}
              disabled={deleting}
              className="inline-flex items-center justify-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-500 disabled:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmDelete}
              disabled={
                deleting ||
                loading ||
                error ||
                !previewData ||
                previewData.addressCount === 0 ||
                !effectiveTenantId
              }
              className="inline-flex items-center justify-center px-3 py-1.5 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-brand-red hover:bg-red-800 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-brand-red disabled:bg-gray-400 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {deleting ? "Deleting..." : "Confirm Delete"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default DeleteAllAddressesModal;

